/**
 * Google Ads & GTM Conversion Tracking
 * 
 * This module provides functions to track conversions for Google Ads
 * via Google Tag Manager (GTM) dataLayer.
 * 
 * GTM ID: GTM-WKLK5FVZ
 * Google Ads ID: AW-18051969661
 */

// Ensure dataLayer exists
const getDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Track WhatsApp button click
 * @param {string} location - Where the click happened (hero, footer, floating, etc.)
 */
export const trackWhatsAppClick = (location = 'unknown') => {
  const dataLayer = getDataLayer();
  
  // Push event to dataLayer for GTM
  dataLayer.push({
    event: 'whatsapp_click',
    event_category: 'engagement',
    event_label: location,
    conversion_type: 'lead'
  });

  // Direct Google Ads conversion tracking (backup)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-18051969661/AKpCKiCqJwcEP3k7J9D',
      'event_callback': () => {
        console.log('WhatsApp click conversion tracked');
      }
    });
  }

  console.log(`[Analytics] WhatsApp click tracked from: ${location}`);
};

/**
 * Track contact form submission
 * @param {string} formType - Type of form (villa_inquiry, contact_page, etc.)
 * @param {object} formData - Form data (villa name, etc.)
 */
export const trackFormSubmit = (formType = 'contact', formData = {}) => {
  const dataLayer = getDataLayer();
  
  // Push event to dataLayer for GTM
  dataLayer.push({
    event: 'form_submit',
    event_category: 'conversion',
    event_label: formType,
    form_type: formType,
    villa_name: formData.villaName || '',
    conversion_type: 'lead'
  });

  // Direct Google Ads conversion tracking (backup)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-18051969661/AKpCKiCqJwcEP3k7J9D',
      'event_callback': () => {
        console.log('Form submit conversion tracked');
      }
    });
  }

  console.log(`[Analytics] Form submit tracked: ${formType}`, formData);
};

/**
 * Track phone call click
 * @param {string} location - Where the click happened
 */
export const trackPhoneClick = (location = 'unknown') => {
  const dataLayer = getDataLayer();
  
  dataLayer.push({
    event: 'phone_click',
    event_category: 'engagement',
    event_label: location,
    conversion_type: 'lead'
  });

  console.log(`[Analytics] Phone click tracked from: ${location}`);
};

/**
 * Track email click
 * @param {string} location - Where the click happened
 */
export const trackEmailClick = (location = 'unknown') => {
  const dataLayer = getDataLayer();
  
  dataLayer.push({
    event: 'email_click',
    event_category: 'engagement',
    event_label: location
  });

  console.log(`[Analytics] Email click tracked from: ${location}`);
};

/**
 * Track page view with enhanced data
 * @param {string} pageName - Name of the page
 * @param {object} pageData - Additional page data
 */
export const trackPageView = (pageName, pageData = {}) => {
  const dataLayer = getDataLayer();
  
  dataLayer.push({
    event: 'page_view',
    page_name: pageName,
    ...pageData
  });
};

/**
 * Track villa view
 * @param {object} villa - Villa data
 */
export const trackVillaView = (villa) => {
  const dataLayer = getDataLayer();
  
  dataLayer.push({
    event: 'villa_view',
    event_category: 'engagement',
    villa_id: villa.id,
    villa_name: villa.title_en || villa.slug,
    villa_price: villa.price || 'on_request',
    villa_location: villa.location_en
  });

  console.log(`[Analytics] Villa view tracked: ${villa.title_en || villa.slug}`);
};
